/**
 * Søknader: the action queue (owner, 2026-09-21; manager redesign, 2026-10-09). Three lists,
 * Krever handling with the count, Kommende and Historikk, as one divided table; what a request
 * does to the rota is in the row, and the decision belongs in the drawer, where the shifts it hits
 * and what it takes from the balance stand above the buttons. The row has one action, Åpne. On a
 * phone each row is a card with the person, the dates, the status, the impact and Åpne together.
 *
 * The amount in Påvirkning is the drawer's: the deduction the server will take, read with the same
 * query the drawer's approve button waits on, never computed here.
 */
import type { ApiAbsence, ApiAbsenceType, ApiShift, ApiUser } from '@/api/data';
import { T } from '@/i18n';
import { oslo } from '@/rules';
import { Empty } from '@/ui';
import { fullName, num } from '@/ui/format';
import { useSearchParams } from 'react-router-dom';
import { daysOf } from './AbsenceDrawer';
import { type Kind, describe, kindOfAbsence, rangeOf } from './AbsenceRange';
import { useDeductionState } from './Deduction';
import { catOf } from './category';
import { dayText, periodText, spanOf } from './dates';
import { type Filter, absenceMatches, teamsOf } from './filters';
import { CatIcon, FilterBar, Person, StatusChip } from './parts';
import { BUCKETS, type Bucket, conflictsOf, queueOf } from './queue';

const HEADS: Record<Bucket, string> = {
  handling: 'Forespørsler som trenger svar',
  kommende: 'Godkjent fravær fremover',
  historikk: 'Tidligere fravær og avgjørelser',
};

export function Soknader({
  all,
  users,
  types,
  shifts,
  filter,
  onFilter,
  filtersOpen,
  onFiltersOpen,
  teams,
  today,
  cancelIds,
  tenantId,
  loading,
  onOpen,
}: {
  all: ApiAbsence[];
  users: ApiUser[];
  types: ApiAbsenceType[];
  shifts: ApiShift[];
  filter: Filter;
  onFilter: (f: Filter) => void;
  filtersOpen: boolean;
  onFiltersOpen: (open: boolean) => void;
  teams: string[];
  today: string;
  cancelIds: ReadonlySet<number>;
  tenantId: number | undefined;
  loading: boolean;
  onOpen: (a: ApiAbsence) => void;
}) {
  const [sp, setSp] = useSearchParams();
  const bucket = (BUCKETS.some(([k]) => k === sp.get('vis')) ? sp.get('vis') : 'handling') as Bucket;
  const setBucket = (b: Bucket) =>
    setSp(
      (p) => {
        if (b === 'handling') p.delete('vis');
        else p.set('vis', b);
        return p;
      },
      { replace: true },
    );
  const userOf = (id: number) => users.find((u) => u.id === id);
  const shown = all.filter((a) => absenceMatches(a, userOf(a.user_id), filter, tenantId));
  const lists = Object.fromEntries(BUCKETS.map(([k]) => [k, queueOf(shown, k, today, cancelIds)])) as Record<
    Bucket,
    ApiAbsence[]
  >;
  const rows = lists[bucket];
  const filtered = !!(filter.q || filter.team || filter.cat);

  return (
    <>
      <FilterBar
        value={filter}
        onChange={onFilter}
        teams={teams}
        placeholder={T('Søk ansatt, type eller merknad')}
        open={filtersOpen}
        onOpen={onFiltersOpen}
      />
      <fieldset className="mchips fchips" aria-label={T('Vis')}>
        {BUCKETS.map(([k, label]) => (
          <button key={k} type="button" aria-pressed={bucket === k} onClick={() => setBucket(k)}>
            {T(label)}
            <span className="mtc">{lists[k].length}</span>
          </button>
        ))}
      </fieldset>
      <section className="mcard">
        <div className="mch">
          <h2>{T(HEADS[bucket])}</h2>
        </div>
        {loading ? (
          <Empty>{T('Henter søknader…')}</Empty>
        ) : rows.length ? (
          <table className="mt cards fq">
            <thead>
              <tr>
                <th>{T('Ansatt')}</th>
                <th>{T('Type')}</th>
                <th>{T('Periode')}</th>
                <th>{T('Status')}</th>
                <th>{T('Påvirkning')}</th>
                <th>
                  <span className="sr">{T('Handlinger')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <QueueRow
                  key={a.id}
                  a={a}
                  u={userOf(a.user_id)}
                  kind={kindOfAbsence(a, types)}
                  type={types.find((t) => t.id === a.absence_type?.id)}
                  shifts={shifts}
                  cancel={cancelIds.has(a.id)}
                  tenantId={tenantId}
                  onOpen={onOpen}
                />
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>
            {filtered
              ? T('Ingen søknader passer filteret.')
              : bucket === 'handling'
                ? T('Ingen søknader venter. Alt er besvart.')
                : bucket === 'kommende'
                  ? T('Ikke noe innvilget fravær fremover.')
                  : T('Ingen tidligere fravær.')}
          </Empty>
        )}
      </section>
    </>
  );
}

function QueueRow({
  a,
  u,
  kind,
  type,
  shifts,
  cancel,
  tenantId,
  onOpen,
}: {
  a: ApiAbsence;
  u: ApiUser | undefined;
  kind: Kind;
  type: ApiAbsenceType | undefined;
  shifts: ApiShift[];
  cancel: boolean;
  tenantId: number | undefined;
  onOpen: (a: ApiAbsence) => void;
}) {
  const { from, to } = spanOf(a);
  const over = conflictsOf(a, kind, shifts);
  const pending = a.status === 'pending';
  const deductible = !!(type?.is_deductible ?? a.absence_type?.is_deductible);
  // the same query the drawer's approve button holds on, so the amount shown is the amount sent
  const charge = useDeductionState(
    pending
      ? { absence_type_id: a.absence_type?.id ?? null, start_date: a.start_date, end_date: a.end_date }
      : null,
    deductible,
  );
  const name = fullName(a.user ?? u);
  // the name the request carries is the type as it was booked; the catalogue's is the fallback
  const typeName = a.absence_type?.name ?? type?.name ?? T('Fravær');
  const n = kind === 'date' ? daysOf(from, to) : null;
  // a part of a day, or across midnight: the clocks and the hours between the two instants
  const hours = kind === 'date' ? null : (Date.parse(a.end_date) - Date.parse(a.start_date)) / 36e5;
  const period = kind === 'date' ? periodText(from, to) : describe(rangeOf(a), kind);
  const chargeText = pending
    ? charge.days != null && charge.days > 0
      ? T('Trekker {n} feriedager', { n: charge.days })
      : ''
    : a.status === 'accepted' && a.charged_days
      ? T('Trukket {n} feriedager', { n: a.charged_days })
      : '';
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the row click is a pointer shortcut; the keyboard path is the Åpne button
    <tr
      className="row"
      data-testid={pending ? 'pending-row' : cancel ? 'cancel-row' : 'absence-row'}
      onClick={() => onOpen(a)}
    >
      <td className="fq-who">
        <Person
          id={a.user_id}
          name={name}
          sub={
            teamsOf(u, tenantId)
              .map((t) => t.name)
              .join(', ') || undefined
          }
        />
      </td>
      <td className="fq-type">
        <span className="fcat">
          <CatIcon cat={catOf(a.absence_type)} />
          <span>
            {typeName}
            {cancel && <small>{T('Ønsker å avlyse')}</small>}
          </span>
        </span>
      </td>
      <td className="fq-per">
        {period}
        <small>
          {n != null
            ? `${n} ${n === 1 ? T('dag') : T('dager')}`
            : hours != null && hours > 0
              ? `${num(hours, 1)} ${T('timer')}`
              : T('timer')}
        </small>
      </td>
      <td className="fq-st">
        <StatusChip status={a.status} cancel={cancel} />
      </td>
      <td className="fq-imp" data-k={T('Påvirkning')}>
        <span className={over.length ? 'fwarn' : ''}>
          {over.length === 0
            ? T('Ingen planlagt vakt')
            : over.length === 1
              ? T('1 vakt berørt')
              : T('{n} vakter berørt', { n: over.length })}
        </span>
        {(chargeText || over.length > 0) && (
          <small>{chargeText || dayText(oslo(over[0]?.start_time)?.date ?? '')}</small>
        )}
      </td>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stops the row click; the button inside is the keyboard path */}
      <td className="fq-act" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="mlink"
          aria-label={T('Åpne {type} for {name}, {period}', { type: typeName, name, period })}
          onClick={() => onOpen(a)}
        >
          {T('Åpne')} <span aria-hidden="true">→</span>
        </button>
      </td>
    </tr>
  );
}
